import { cx } from 'utils';

/**
 * Card — clean white surface container for grouped content.
 *
 * Composes with optional sub-parts (Card.Header, Card.Title, Card.Body,
 * Card.Footer) for consistent internal spacing, or use plain children.
 *
 * @param {'solid'|'glass'|'gradient'|'outline'|'elevated'|'navy'} [variant='solid']
 * @param {boolean} [hover=false]   Lift on hover (good for clickable cards).
 * @param {boolean} [glow=false]    Stronger resting elevation.
 * @param {'none'|'sm'|'md'|'lg'} [padding='md']
 */
const VARIANTS = {
  // Clean white surface — hairline border + very subtle shadow (SaaS-standard).
  solid: 'border border-slate-200 bg-white text-slate-700 shadow-card',
  glass: 'border border-slate-200 bg-white text-slate-700 shadow-card',
  gradient: 'border border-brand-100/60 bg-brand-gradient-soft text-slate-700',
  // Flat — border only, for dense/nested tiles where a shadow would stack.
  outline: 'border border-slate-200 bg-white text-slate-700',
  elevated: 'border border-slate-200 bg-white text-slate-700 shadow-lift',
  // Deep slate hero surface — mentor earnings tile etc.
  navy: 'border border-navy-700/50 bg-navy-gradient text-white/85 shadow-card',
};

const PADDING = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

export default function Card({
  as: Component = 'div',
  variant = 'solid',
  padding = 'md',
  hover = false,
  glow = false,
  className = '',
  children,
  ...props
}) {
  const classes = cx(
    'relative rounded-2xl',
    VARIANTS[variant] || VARIANTS.solid,
    PADDING[padding] ?? PADDING.md,
    hover &&
      'cursor-pointer transition-all duration-200 ease-premium hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-lift',
    glow && 'shadow-card',
    className
  );

  return (
    <Component className={classes} {...props}>
      {children}
    </Component>
  );
}

/** Card.Header — title block with optional trailing action slot. */
function CardHeader({ className = '', action, children, ...props }) {
  return (
    <div className={cx('mb-4 flex items-start justify-between gap-4', className)} {...props}>
      <div className="min-w-0">{children}</div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

function CardTitle({ className = '', children, ...props }) {
  return (
    <h3 className={cx('font-display text-lg font-semibold text-slate-900', className)} {...props}>
      {children}
    </h3>
  );
}

function CardDescription({ className = '', children, ...props }) {
  return (
    <p className={cx('mt-1 text-sm text-slate-500', className)} {...props}>
      {children}
    </p>
  );
}

function CardBody({ className = '', children, ...props }) {
  return (
    <div className={cx('text-sm text-slate-600', className)} {...props}>
      {children}
    </div>
  );
}

function CardFooter({ className = '', children, ...props }) {
  return (
    <div
      className={cx('mt-6 flex items-center gap-3 border-t border-slate-100/80 pt-4', className)}
      {...props}
    >
      {children}
    </div>
  );
}

Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Description = CardDescription;
Card.Body = CardBody;
Card.Footer = CardFooter;
